import { ComponentPropsWithoutRef, CSSProperties } from 'react'
import { cn } from '~shared/utils/cn'
import Link from 'next/link'
import Image from 'next/image'
import { TValueOfPromise } from 'fractals'
import {
  getAllJournals,
  getJournalsData,
  getTagsData,
  getFacultiesData as getFacultiesFromJournalsData
} from '@/modules/journals-page/widgets/Journals'
import { Button, ButtonProps } from '~shared/components/button-v2'
import { replacePrepositionsWhitespaces } from '~shared/utils/replace-prepositions-whitespaces'
import { Clock2Icon } from '~shared/icons/clock2'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from '@/modules/components/ui/carousel'
import dayjs from 'dayjs'

export interface JournalCardProps extends ComponentPropsWithoutRef<'div'> {
  model: NonNullable<TValueOfPromise<typeof getJournalsData>['value']>[number]
}

export const JournalCard = (props: JournalCardProps) => {
  const { className, model, ...rest } = props

  return (
    <div
      className={cn(
        'relative justify-between bg-secondary rounded-[24px] overflow-hidden group',
        className
      )}
      {...rest}>
      <Link
        href={`/journal--${model.faculty.slug}--${model.slug}`}
        prefetch={false}>
        <div className='relative rounded-[24px] overflow-hidden h-[203px] bg-tertiary'>
          <Image
            src={model.image.url || ''}
            alt='helo'
            fill
            className='object-cover scale-110 group-hover:scale-125 transition-transform duration-300'
          />
        </div>

        <div className='p-6 pt-0 flex flex-col pb-[67px]'>
          <span
            className='flex items-center justify-center mt-6 self-start text-[16px] leading-[1.2] px-[10px] h-[37px] text-white rounded-[24px]'
            style={{
              backgroundColor: model.faculty.color || 'white'
            }}>
            {model.faculty.name}
          </span>

          <p className='overflow-hidden mt-6 font-medium text-[16px] md:text-[20px] xl:text-[24px] tracking-[-0.01em] leading-[1] line-clamp-3'>
            {replacePrepositionsWhitespaces(model.name)}
          </p>

          {/* <p className='mt-4 text-[16px] leading-[1.1] line-clamp-2'>
          Узнайте что-то новое для себя в самой свежей статье от MITM институт
        </p> */}
        </div>
        <div className='absolute left-6 bottom-6 flex gap-6 mt-auto text-[14px] md:text-[16px] leading-[1.2] text-[#6A6A6A]'>
          <p>{dayjs(model.createdDate).format('DD.MM.YYYY')}</p>
          <div className='whitespace-nowrap flex items-center gap-[4px]'>
            <Clock2Icon />
            {`${Number(model.time?.split(':')[1] || 5)} мин.`}
          </div>
        </div>
      </Link>
    </div>
  )
}

const Chip = ({
  className,
  name,
  slug,
  style
}: {
  name: string
  slug?: string
} & ButtonProps) => (
  <Link
    href={slug ? `journal--${slug}` : 'journal'}
    prefetch={false}
    className={cn(
      'cursor-pointer transition-all before:transition-all before:duration-300 duration-300 relative text-[14px] md:text-[16px] h-[37px] px-[16px] flex items-center justify-center rounded-[24px]',
      className
    )}
    style={style}>
    {name}
  </Link>
)
export interface JournalSectionProps
  extends ComponentPropsWithoutRef<'section'> {
  journals: TValueOfPromise<typeof getJournalsData>['value']
  allJournals: TValueOfPromise<typeof getAllJournals>['value']
  facultiesFromJournals: TValueOfPromise<
    typeof getFacultiesFromJournalsData
  >['value']
  tags: TValueOfPromise<typeof getTagsData>['value']
}

export const JournalSection = (props: JournalSectionProps) => {
  const {
    journals,
    allJournals,
    className,
    facultiesFromJournals,
    tags,
    ...rest
  } = props
  if (!journals) return null

  const itemClassName =
    'flex-grow-[1] flex-shrink-0 basis-1/4 md:flex-shrink-[1] md:flex-grow-0 md:basis-auto'

  return (
    <section className={cn('container container-slim', className)} {...rest}>
      <h2 className='typo-section-heading text-center'>Журнал</h2>
      <div className='mt-[30px] md:mt-[24px] lg:mt-[40px] xl:mt-[60px] flex overflow-x-auto overflow-y-hidden md:flex-wrap gap-[8px] xl:gap-[16px] md:justify-center whitespace-nowrap'>
        <Chip
          name='Все статьи'
          className={cn(
            'text-primary bg-accent hover:bg-tertiary',
            itemClassName
          )}
        />
        <Chip
          name='Про образование'
          slug='pro-obrazovanie'
          className={cn(
            'text-[#424242] bg-black/10 hover:bg-[#424242] hover:text-primary',
            itemClassName
          )}
        />
        {facultiesFromJournals?.map(faculty => (
          <Chip
            key={faculty.slug!}
            name={faculty.name!}
            slug={faculty.slug!}
            style={
              {
                '--color': faculty.color!
              } as CSSProperties
            }
            className={cn(
              'before:z-0 z-[1] before:absolute before:rounded-[24px] before:inset-0 before:opacity-10 hover:before:opacity-0 hover:bg-[var(--color)] text-[var(--color)] hover:text-primary before:bg-[var(--color)]',
              itemClassName
            )}
          />
        ))}
      </div>
      <Carousel
        className='mt-[16px] md:mt-[24px] lg:mt-[40px] xl:mt-[60px]'
        aria-label='Журнал'>
        <CarouselContent>
          {journals?.map(journal => (
            <CarouselItem
              key={journal.slug}
              className='basis-full md:basis-1/2 lg:basis-1/3 select-none'>
              <JournalCard model={journal} className='h-full' />
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className='flex gap-4 mx-auto w-fit mt-[30px] md:mt-[40px] xl:mt-[60px]'>
          <CarouselPrevious size='lg' />
          <CarouselNext size='lg' />
        </div>
      </Carousel>
    </section>
  )
}
